import { z } from "zod";
//#region src/token-self-check.ts
/**
* `GET /v1/auth/me/token-status` — the CP self-check a resource server calls
* before executing a WRITE on a delegated credential (#1085 + #1109, spine D-1).
*
* ## ⛔ THIS IS NOT RFC 7662, AND IT MUST NOT BE NAMED AS IF IT WERE
*
* RFC 7662 introspection is a **client-authenticated** endpoint: a resource
* server presents *its own* credentials and asks about *someone else's* token.
* This route is the opposite shape — it is **self-scoped**, authenticated by the
* caller's OWN bearer, and answers only about that bearer. It borrows RFC 7662's
* *response* field names (`active`, `sub`, `client_id`, `exp`, `scope`) because
* they are good names and a reader recognises them, and nothing else.
*
* ⛔ Do not rename this to `/introspect`, and do not add a `token` request
* parameter. The self-scoping is the security argument, not an incidental
* simplification: `auth.controller.ts` records why a proxied introspection route
* is broken here — `PermissionsProxyService.proxy` mints its OWN service token
* and does not forward the caller's bearer, so a service-authenticated variant
* resolves the SERVICE principal and would answer about the wrong subject while
* looking like it worked. The blessed precedent is `GET /v1/auth/me/grants`,
* which `infra/docker/registry-auth-proxy` already uses the same way: *"the
* caller's own bearer, and nothing else."*
*
* ## ⛔ WHY `inactive` CARRIES NOTHING — and why that is a TYPE, not a convention
*
* The two members are a DISCRIMINATED UNION on `active`, so an inactive answer is
* *structurally incapable* of carrying `sub`, `client_id`, `exp` or `scope`. Two
* reasons, and both have bitten this platform before:
*
*  1. **RFC 7662 §2.2 requires it** — "if the token is not active, no other
*     information is returned."
*  2. **A single response object with optional fields makes "why is it inactive"
*     observable.** Revoked, expired, client-deleted and never-existed must be
*     byte-identical, exactly as the 404-not-403 discipline requires elsewhere in
*     this repo. An `active: false` that still echoed `client_id` would confirm
*     the client exists.
*
* ⇒ `{ active: false }` is the WHOLE answer. There is no `reason` field and
* there must not be one.
*
* ## ⚠️ THE CALLER MUST TREAT A 401/403 AS INACTIVE, NOT AS AN OUTAGE
*
* A revoked **class-2** agent credential never reaches this handler at all:
* cp-auth-api binds `CP_MCP_TOKEN_STORE`, so `CpMcpTokenGuard` 401s it first. So
* *inactive* arrives over the wire in two shapes — a `401`/`403`, or a `200` with
* `{active: false}` — and a consumer that mapped only the second would let a
* revoked agent token through. `DelegatedTokenChecker`'s HTTP implementation
* folds both, and a test drives each shape separately.
*
* Contrast: a transport failure, a 5xx, or any body this schema rejects is
* **could-not-determine**, which is a different thing again and must fail CLOSED.
* "Did not look" is never "looked and found nothing" — the same rule
* `bin/security-scan`'s exit-3 contract exists for.
*
* ## ⭐ `sub` IS LOAD-BEARING — the consumer MUST compare it
*
* The guard reads `active`, but it must also assert that `sub` equals the `sub`
* of the token it presented, and treat a mismatch as could-not-determine (fail
* closed). Without that, "an active answer" and "an active answer **about
* somebody else's token**" are indistinguishable — and that is not a
* hypothetical failure here: `auth.controller.ts` records the live version of it,
* where `PermissionsProxyService.proxy` mints its own service token and a
* proxied self route resolves the SERVICE principal while looking like it works.
* ⛔ Do not "simplify" `sub` out of this response, and do not drop the compare.
*/
/**
* ⚠️ `.strict()` on BOTH members, deliberately.
*
* The producer is a NestJS handler in cp-auth-api and the consumer is a guard in
* four other services; the only thing holding them together is this schema. A
* stripping object would silently discard a field the producer started sending
* and a field the producer stopped sending would surface as `undefined` rather
* than a parse failure — so the fake used in tests could model a shape the real
* endpoint cannot produce. `.strict()` plus a discriminated union makes the
* failure loud on both sides.
*/
const tokenSelfCheckResponseSchema = z.discriminatedUnion("active", [z.object({ active: z.literal(false) }).strict(), z.object({
	active: z.literal(true),
	/** The operator id — the `sub` claim of the presented token. */
	sub: z.string().min(1),
	/**
	* The OAuth client the token was issued to (the `azp` claim), when the
	* credential came from the authorization-code flow.
	*
	* ⚠️ ABSENT for a class-2 MCP agent credential, which is minted directly by
	* an operator with no OAuth client involved — and absent for a first-party
	* operator token. Optional here rather than nullable so those cases omit
	* the key entirely, matching RFC 7662's "no other information" instinct.
	*/
	client_id: z.string().min(1).optional(),
	/** Expiry, seconds since the epoch — the `exp` claim, echoed unchanged. */
	exp: z.number().int().positive(),
	/**
	* The delegated scope set, SPACE-DELIMITED per RFC 7662 §2.2 — not a JSON
	* array, even though the `scp` claim is one.
	*
	* ⚠️ An EMPTY STRING is a legal and reachable value, and it is NOT the same
	* as an absent key: the delegated mint produces a *"gracefully empty"* token
	* when the operator's permissions no longer intersect the consented scopes
	* (`oauth-as-token.service.ts`), and that token is still ACTIVE — it is
	* powerless, not revoked. A consumer must not read empty-scope as inactive.
	*
	* ⚠️ ABSENT means *not a delegated credential at all* — a first-party
	* operator or service token, which carries no `scp` claim and to which no
	* scope narrowing applies. Rendering that as `''` would claim the token
	* holds nothing, which is the opposite of the truth.
	*/
	scope: z.string().optional()
}).strict()]);
/** The one path, so the producer's route and the consumer's URL cannot drift. */
const TOKEN_SELF_CHECK_PATH = "/v1/auth/me/token-status";
//#endregion
export { TOKEN_SELF_CHECK_PATH, tokenSelfCheckResponseSchema };

//# sourceMappingURL=token-self-check.js.map